import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import BlockIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import PersonOffIcon from '@mui/icons-material/PersonOffOutlined';
import { toast } from 'react-toastify';
import {
  PageHeader,
  StatusBadge,
  ConfirmDialog,
} from '../../components/ui';
import { providersApi } from '../../api/providers';
import { formatDate, initials } from '../../utils';
import type { AccountStatus, ServiceProvider, VerificationDocument } from '../../types';

interface TabPanelProps {
  value: number;
  index: number;
  children: React.ReactNode;
}

const TabPanel: React.FC<TabPanelProps> = ({ value, index, children }) => {
  if (value !== index) return null;
  return <Box sx={{ pt: 2.5 }}>{children}</Box>;
};

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1" fontWeight={500} sx={{ mt: 0.25 }}>
      {value}
    </Typography>
  </Box>
);

type AccountConfirm = {
  status: AccountStatus;
  title: string;
  message: string;
  confirmLabel: string;
  danger?: boolean;
};

const ProviderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [provider, setProvider] = useState<ServiceProvider | null>(null);
  const [tab, setTab] = useState(0);
  const [confirm, setConfirm] = useState<AccountConfirm | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [rejectDoc, setRejectDoc] = useState<VerificationDocument | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [docLoading, setDocLoading] = useState(false);

  const loadProvider = useCallback(async () => {
    if (!id) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await providersApi.getProviderById(id);
      setProvider(data);
      setNotFound(false);
    } catch {
      setNotFound(true);
      setProvider(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadProvider();
  }, [loadProvider]);

  const openAccountConfirm = (status: AccountStatus) => {
    if (!provider) return;
    const map: Record<AccountStatus, Omit<AccountConfirm, 'status'>> = {
      Suspended: {
        title: 'Suspend provider',
        message: `Suspend ${provider.name}? They will temporarily lose access.`,
        confirmLabel: 'Suspend',
        danger: true,
      },
      Active: {
        title: 'Reactivate provider',
        message: `Reactivate ${provider.name}? They will regain full access.`,
        confirmLabel: 'Reactivate',
      },
      Deactivated: {
        title: 'Deactivate provider',
        message: `Deactivate ${provider.name}? This disables the account.`,
        confirmLabel: 'Deactivate',
        danger: true,
      },
      Inactive: {
        title: 'Update status',
        message: `Set ${provider.name} to Inactive?`,
        confirmLabel: 'Confirm',
      },
    };
    setConfirm({ status, ...map[status] });
  };

  const handleAccountConfirm = async () => {
    if (!confirm || !provider) return;
    setConfirmLoading(true);
    try {
      const shouldBlock = confirm.status === 'Suspended' || confirm.status === 'Deactivated';
      await providersApi.toggleBlockProvider(provider.id, shouldBlock);
      const updated = await providersApi.getProviderById(provider.id);
      setProvider(updated);
      toast.success(`Provider is now ${confirm.status}`);
      setConfirm(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update account status');
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleApproveDocument = async (doc: VerificationDocument) => {
    if (!provider) return;
    setDocLoading(true);
    try {
      toast.info('Document review API is not available yet');
    } finally {
      setDocLoading(false);
    }
  };

  const handleRejectDocument = async () => {
    if (!provider || !rejectDoc) return;
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setDocLoading(true);
    try {
      toast.info('Document review API is not available yet');
      setRejectDoc(null);
      setRejectReason('');
    } finally {
      setDocLoading(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <PageHeader title="Provider Details" subtitle="Loading…" />
      </Box>
    );
  }

  if (notFound || !provider) {
    return (
      <Box>
        <PageHeader
          title="Provider not found"
          subtitle="This provider does not exist or was removed"
          breadcrumbs={[
            { label: 'Providers', to: '/providers' },
            { label: 'Not found' },
          ]}
          actions={
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/providers')}
              variant="outlined"
            >
              Back to Providers
            </Button>
          }
        />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={provider.name}
        subtitle={provider.primaryCategory}
        breadcrumbs={[
          { label: 'Providers', to: '/providers' },
          { label: provider.name },
        ]}
        actions={
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/providers')}
            variant="outlined"
          >
            Back
          </Button>
        }
      />

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Personal" />
        <Tab label="Professional" />
        <Tab label="Documents" />
        <Tab label="Account" />
      </Tabs>

      <TabPanel value={tab} index={0}>
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 3,
              }}
            >
              <Avatar
                src={provider.avatar}
                sx={{
                  width: 72,
                  height: 72,
                  fontSize: 24,
                  bgcolor: 'secondary.main',
                }}
              >
                {initials(provider.name)}
              </Avatar>
              <Box>
                <Typography variant="h3">{provider.name}</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <StatusBadge status={provider.status} />
                  <StatusBadge status={provider.verificationStatus} />
                </Stack>
              </Box>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <InfoRow label="Email" value={provider.email} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InfoRow label="Phone" value={provider.phone} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InfoRow label="Provider ID" value={provider.id} />
              </Grid>
              <Grid item xs={12}>
                <InfoRow label="Address" value={provider.address} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <InfoRow
                  label="Primary Category"
                  value={provider.primaryCategory}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoRow
                  label="Experience"
                  value={`${provider.experienceYears} years`}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoRow label="Service Area" value={provider.serviceArea} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoRow
                  label="Application Date"
                  value={formatDate(provider.applicationDate)}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Categories
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  {provider.categories.map((c) => (
                    <Chip key={c} label={c} size="small" variant="outlined" />
                  ))}
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Skills
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  {provider.skills.map((s) => (
                    <Chip key={s} label={s} size="small" color="primary" variant="outlined" />
                  ))}
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <InfoRow label="About" value={provider.about} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tab} index={2}>
        <Grid container spacing={2}>
          {provider.documents.map((doc) => (
            <Grid item xs={12} md={6} key={doc.id}>
              <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 1.5,
                    }}
                  >
                    <Typography variant="h3">{doc.type}</Typography>
                    <StatusBadge status={doc.status} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Uploaded {formatDate(doc.uploadedAt)}
                  </Typography>
                  {doc.rejectionReason && (
                    <Typography
                      variant="body2"
                      color="error.main"
                      sx={{ mt: 1 }}
                    >
                      Reason: {doc.rejectionReason}
                    </Typography>
                  )}
                  {doc.status === 'Pending' && (
                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<CheckIcon />}
                        disabled={docLoading}
                        onClick={() => handleApproveDocument(doc)}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<CloseIcon />}
                        disabled={docLoading}
                        onClick={() => {
                          setRejectDoc(doc);
                          setRejectReason('');
                        }}
                      >
                        Reject
                      </Button>
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      <TabPanel value={tab} index={3}>
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" color="text.secondary">
                Current account status
              </Typography>
              <Box sx={{ mt: 1 }}>
                <StatusBadge status={provider.status} />
              </Box>
            </Box>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<BlockIcon />}
                disabled={
                  provider.status === 'Suspended' ||
                  provider.status === 'Deactivated'
                }
                onClick={() => openAccountConfirm('Suspended')}
              >
                Suspend
              </Button>
              <Button
                variant="outlined"
                color="success"
                startIcon={<CheckCircleIcon />}
                disabled={provider.status === 'Active'}
                onClick={() => openAccountConfirm('Active')}
              >
                Reactivate
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<PersonOffIcon />}
                disabled={provider.status === 'Deactivated'}
                onClick={() => openAccountConfirm('Deactivated')}
              >
                Deactivate
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </TabPanel>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title || ''}
        message={confirm?.message || ''}
        confirmLabel={confirm?.confirmLabel}
        danger={confirm?.danger}
        loading={confirmLoading}
        onConfirm={handleAccountConfirm}
        onClose={() => !confirmLoading && setConfirm(null)}
      />

      <Dialog
        open={Boolean(rejectDoc)}
        onClose={docLoading ? undefined : () => setRejectDoc(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject document</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Provide a reason for rejecting {rejectDoc?.type}.
          </Typography>
          <TextField
            label="Rejection reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRejectDoc(null)} disabled={docLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectDocument}
            disabled={docLoading}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProviderDetailPage;
